/**
 * net/remote-goose.js — the visual body every non-local goose wears.
 *
 * Two consumers: `presence.js` (real peers, driven by interpolated network
 * poses) and `npc-geese.js` (ambient locals, driven by a waypoint plan). Both
 * want the same thing — a `factory.createGoose()` with cosmetics mounted, a
 * billboarded name tag, a speech bubble, and the same waddle bob the player's
 * own goose uses — so that lives here once as `GooseAvatar`.
 *
 * Nothing in this file touches physics. A remote goose has no body, no
 * collider and no tag; it cannot push the player, block a stall, or interact
 * with a throw. That is the GDD 10.4 contract stated as an absence: there is
 * simply nothing to widen.
 *
 * @module net/remote-goose
 */

import * as THREE from 'three';
import { AVATAR, NET, NPC, PALETTE } from '../constants.js';
import * as factory from '../assets3d/factory.js';
import { createNameTag, createSpeechBubble } from './labels.js';

/** Body tints so four geese in one frame read as four individuals. */
export const GOOSE_TINTS = Object.freeze([
  PALETTE.gooseBody, 0xe7e2d4, 0xf0e6f2, 0xdfe9ef, 0xf5efe0, 0xe4ecdf,
]);

/**
 * Greybox cosmetic stand-in for a remote or NPC goose.
 *
 * `avatar/cosmetics.js` keeps its builder registry private and is bound to the
 * single local goose, so remote geese get their own minimal renderer here
 * rather than an edit to a module this agent does not own. The id is hashed to
 * a hue, which is enough for "that goose is wearing something different from
 * mine" — the show-off signal GDD 3.4 is actually asking for.
 *
 * @param {string} slot
 * @param {string} id
 * @returns {THREE.Object3D|null}
 */
export function buildRemoteCosmetic(slot, id) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  const color = new THREE.Color().setHSL((h % 360) / 360, 0.62, 0.58);

  if (slot === 'aura') {
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(0.42, 0.02, 6, 20),
      new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.55 }),
    );
    ring.rotation.x = Math.PI / 2;
    ring.userData.spin = true;
    return ring;
  }
  if (slot === 'head') {
    const hat = new THREE.Mesh(new THREE.ConeGeometry(0.1, 0.16, 8), factory.mat(color.getHex(), { rough: 0.7 }));
    hat.position.y = 0.09;
    return hat;
  }
  if (slot === 'beak') {
    const tip = new THREE.Mesh(new THREE.SphereGeometry(0.035, 8, 6), factory.mat(color.getHex(), { rough: 0.5 }));
    return tip;
  }
  const box = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.05, 0.09), factory.mat(color.getHex(), { rough: 0.6 }));
  return box;
}

/**
 * A complete non-local goose: mesh, cosmetics, name tag, speech bubble, bob.
 *
 * Position and facing are properties you write; `update(dt)` turns them into
 * the same waddle the player's goose performs, so a remote goose crossing the
 * midway is indistinguishable from a local one.
 */
export class GooseAvatar {
  /**
   * @param {{ name?:string, cosmetics?:object, color?:number,
   *           tagColor?:string, tagBorder?:string }} [opts]
   */
  constructor(opts = {}) {
    this.name = opts.name || 'Goose';

    /** World position of the goose's feet. */
    this.position = new THREE.Vector3();
    /** Facing in radians, using the game's `(sin θ, 0, cos θ)` convention. */
    this.facing = Math.PI;
    /** Metres per second, drives the waddle bob amplitude. */
    this.speed = 0;

    this.group = new THREE.Group();
    this.group.name = `goose-avatar-${this.name}`;

    this.goose = factory.createGoose({ color: opts.color ?? PALETTE.gooseBody });
    this.group.add(this.goose);

    // Labels ride an anchor pinned to the feet rather than the bobbing body, so
    // their own animations (the bubble's rise) are not fighting the waddle.
    this.labelAnchor = new THREE.Group();
    this.labelAnchor.name = 'labels';
    this.group.add(this.labelAnchor);

    this.tag = createNameTag(this.name, {
      color: opts.tagColor ?? '#ffd166',
      border: opts.tagBorder,
    });
    this.labelAnchor.add(this.tag);

    this.bubble = createSpeechBubble();
    this.labelAnchor.add(this.bubble);

    /** @type {Record<string, THREE.Object3D>} */
    this._cosmetics = {};
    this._bobPhase = 0;
    this._auraPhase = 0;
    /** Vertical offset from an emote hop, added on top of the bob. */
    this.hopOffset = 0;

    this.applyCosmetics(opts.cosmetics || {});
  }

  /**
   * Mount (and unmount) cosmetics by slot.
   * @param {Record<string, string>} equipped Slot → cosmetic id.
   */
  applyCosmetics(equipped) {
    const anchors = this.goose.userData.attach || {};
    for (const [slot, anchor] of Object.entries(anchors)) {
      const id = equipped[slot];
      const current = this._cosmetics[slot];
      if (current) {
        anchor.remove(current);
        current.geometry?.dispose();
        delete this._cosmetics[slot];
      }
      if (!id) continue;
      const obj = buildRemoteCosmetic(slot, id);
      if (!obj) continue;
      obj.name = `cosmetic-${slot}-${id}`;
      anchor.add(obj);
      this._cosmetics[slot] = obj;
    }
  }

  /** @param {string} name */
  setName(name) {
    this.name = name;
    this.tag.userData.setText(name);
  }

  /** @param {string} [text] @param {number} [seconds] */
  say(text = 'HONK!', seconds = NPC.bubbleSeconds) {
    this.bubble.userData.show(text, seconds);
  }

  /** @param {boolean} v */
  setVisible(v) {
    this.group.visible = v;
  }

  /**
   * Apply the current position/facing/speed to the mesh, with the player
   * goose's waddle bob and sway so the motion reads the same.
   * @param {number} dt
   */
  update(dt) {
    this.goose.position.copy(this.position);
    this.goose.rotation.y = this.facing + Math.PI;

    if (this.speed > 0.1) {
      this._bobPhase += dt * NPC.bobFrequency;
      const bob = Math.sin(this._bobPhase) * NPC.bobAmplitude;
      this.goose.position.y = this.position.y + Math.abs(bob) + this.hopOffset;
      this.goose.rotation.z = Math.cos(this._bobPhase * 0.5) * 0.055;
      const parts = this.goose.userData.parts;
      if (parts) parts.neck.rotation.x = -0.22 + Math.sin(this._bobPhase) * 0.07;
    } else {
      this.goose.position.y = this.position.y + this.hopOffset;
      this.goose.rotation.z *= Math.max(0, 1 - dt * 6);
    }

    this.labelAnchor.position.copy(this.position);
    this.bubble.userData.update(dt);

    const aura = this._cosmetics.aura;
    if (aura?.userData.spin) {
      this._auraPhase += dt;
      aura.rotation.z = this._auraPhase * 0.9;
    }
  }

  /** Release GPU resources owned by this avatar (not the shared factory ones). */
  dispose() {
    this.tag.userData.dispose?.();
    this.bubble.userData.dispose?.();
    for (const obj of Object.values(this._cosmetics)) obj.geometry?.dispose();
    this._cosmetics = {};
    this.group.removeFromParent();
  }
}

/**
 * A peer's goose, driven by interpolated network poses.
 *
 * Poses arrive at 10 Hz over an unreliable path. Rendering the newest one the
 * instant it lands produces a goose that teleports ten times a second, so
 * snapshots go into a buffer and are rendered `NET.interpDelayMs` in the past,
 * between the two samples that bracket that instant. The cost is 200 ms of
 * latency on someone else's position, which nobody can perceive; the benefit is
 * motion that looks continuous.
 */
export class RemoteGoose extends GooseAvatar {
  /**
   * @param {string} peerId
   * @param {{ name?:string, cosmetics?:object, color?:number }} [opts]
   */
  constructor(peerId, opts = {}) {
    super({ ...opts, tagColor: '#8fe4ff' });
    this.peerId = peerId;
    this.mode = 'third';
    /** @type {string|null} */ this.activeStall = null;
    /** Lane index used when this peer is playing the same stall as us. */
    this.laneIndex = 0;
    this.lastSeen = 0;

    /** @type {Array<{t:number, p:[number,number,number], y:number}>} */
    this._buffer = [];
    /** When set, the goose is pinned to a stall lane instead of its hub pose. */
    this._laneAnchor = null;
  }

  /**
   * Record a pose snapshot.
   * @param {[number,number,number]} position Already clamped by the protocol.
   * @param {number} yaw
   * @param {number} [now]
   */
  pushPose(position, yaw, now = performance.now()) {
    this._buffer.push({ t: now, p: position, y: yaw });
    // Two seconds of history is far more than the interpolation delay needs and
    // bounds the array against a peer that never stops sending.
    const cutoff = now - 2000;
    while (this._buffer.length > 2 && this._buffer[0].t < cutoff) this._buffer.shift();
    this.lastSeen = now;
  }

  /**
   * Pin this goose to a fixed spot — used to seat a peer at the neighbouring
   * lane of the stall you are both playing (GDD 10.4).
   * @param {{ position: THREE.Vector3, yaw: number }|null} anchor
   */
  setLaneAnchor(anchor) {
    this._laneAnchor = anchor;
  }

  /**
   * @param {number} dt
   * @param {number} [now]
   */
  update(dt, now = performance.now()) {
    if (this._laneAnchor) {
      this.position.lerp(this._laneAnchor.position, Math.min(1, dt * 6));
      this.facing = angleLerp(this.facing, this._laneAnchor.yaw, Math.min(1, dt * 6));
      this.speed = 0;
    } else {
      this._sampleBuffer(now, dt);
    }
    super.update(dt);
  }

  /** @private */
  _sampleBuffer(now, dt) {
    const buf = this._buffer;
    if (buf.length === 0) return;

    const renderAt = now - NET.interpDelayMs;
    const previous = this.position.clone();

    if (buf.length === 1 || renderAt <= buf[0].t) {
      this.position.set(buf[0].p[0], buf[0].p[1], buf[0].p[2]);
      this.facing = buf[0].y;
    } else if (renderAt >= buf[buf.length - 1].t) {
      const last = buf[buf.length - 1];
      this.position.set(last.p[0], last.p[1], last.p[2]);
      this.facing = last.y;
    } else {
      let i = buf.length - 1;
      while (i > 0 && buf[i - 1].t > renderAt) i--;
      const a = buf[i - 1];
      const b = buf[i];
      const span = b.t - a.t;
      const k = span > 0 ? (renderAt - a.t) / span : 1;
      this.position.set(
        a.p[0] + (b.p[0] - a.p[0]) * k,
        a.p[1] + (b.p[1] - a.p[1]) * k,
        a.p[2] + (b.p[2] - a.p[2]) * k,
      );
      this.facing = angleLerp(a.y, b.y, k);
    }

    this.speed = dt > 0 ? previous.distanceTo(this.position) / dt : 0;
    // A joining peer's first snapshot is a jump from the origin, not a sprint.
    if (this.speed > AVATAR.moveSpeed * 3) this.speed = 0;
  }
}

/**
 * Shortest-arc interpolation between two headings. This is the slerp a
 * Y-only rotation reduces to, without building two quaternions to get it.
 * @param {number} a @param {number} b @param {number} k
 * @returns {number}
 */
export function angleLerp(a, b, k) {
  let d = (b - a) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return a + d * k;
}
